import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "./axiosBaseQuery";
import DialerTreeConfig from "../app/config";
import Organisation, { TaxExemption } from "@/model/interface/Organisation";
import { Prompt, PromptKind, PromptVersion, SkillAudience, SkillScope, SkillType } from "@/model/interface/Prompt";
import { PlatformConfig } from "@/model/interface/PlatformConfig";
import { OrganisationSettingsOverview, QuietHoursPolicyOption } from "@/model/interface/OrganisationSettings";
import { settingState } from "@/model/interface/SettingState";
import { SystemWorkflowStatus } from "@/model/interface/SystemWorkflow";
import { Charge } from "@/model/interface/Charge";
import { Deposit } from "@/model/interface/Deposit";
import { Invoice } from "@/model/interface/Invoice";
import PagedResponse from "@/model/interface/PagedResponse";
import { PricingPlan } from "@/model/interface/PricingPlan";
import { PlanAssignment, SubscriptionPlan } from "@/model/interface/SubscriptionPlan";
import { StripeSubscriptionData } from "@/model/interface/StripeSubscriptionData";
import { AddOnCatalogEntry, AddOnType, OrgAddOnPriceEntry } from "@/model/interface/AddOn";
import { PlanFeatureOption } from "@/model/interface/PlanFeatureOption";
import { LogEntry } from "@/model/interface/LogEntry";
import type {
  AiAgent,
  AiAgentTemplate,
  AiAgentListing,
  VoiceModelOption,
  VoiceOption,
  VoiceModelsCatalogue,
} from "@/model/interface/AiAgent";
import type { WorkflowExecution } from "@/model/interface/Workflow";
import type Call from "@/model/interface/Call";
import type { RegistrationRequest } from "@/model/interface/RegistrationRequest";
import type { BlockedEmailDomain, BlockedEmailDomainData } from "@/model/interface/BlockedEmailDomain";
import type {
  CallingHoliday,
  CallingHolidayFormData,
  CallingRegionOption,
  CallingRule,
  CallingRuleFormData,
} from "@/model/interface/CallingRule";
import type { FraudSignal } from "@/model/interface/FraudSignal";
import type { BillingEvent } from "@/model/interface/BillingEvent";
import type { AdminWorkflow, AdminWorkflowExecution } from "@/model/interface/PlatformActivity";
import type User from "@/model/interface/User";
import type A2pRegistration from "@/model/interface/A2pRegistration";
import type { A2pEventStreamStatus } from "@/model/interface/A2pRegistration";
import type PhoneNumber from "@/model/interface/PhoneNumber";

export interface AiLatencyStats {
  count: number;
  meanMs: number;
  maxMs: number;
  p50Ms: number;
  p95Ms: number;
  p99Ms: number;
}

export interface AiMetricsResponse {
  e2eTurnLatency: AiLatencyStats;
  sttLatency: AiLatencyStats;
  llmLatency: AiLatencyStats;
  ttsLatency: AiLatencyStats;
  llmRequests: { success: number; error: number; total: number; errorRatePct: number };
  llmErrors: { llmError: number; exception: number };
  ttsRequests: { success: number; error: number; total: number; errorRatePct: number };
  bargeIns: number;
  toolInvocations: Record<string, number>;
  toolIterationCapHits: number;
}

export const adminApiTagTypes = [
  "PlatformAdmin",
  "PlatformCharges",
  "PlatformInvoices",
  "PlatformConfig",
  "PricingPlans",
  "SubscriptionPlans",
  "AiTemplates",
  "AiListings",
  "DeployedAgents",
  "VoiceModels",
  "AddOns",
  "OrgAddOnPrices",
  "OrganisationUsageRates",
  "RegistrationRequests",
  "BlockedEmailDomains",
  "FraudSignals",
  "Prompts",
  "Prompt",
  "PlatformUsers",
  "A2pAdminRegistrations",
  "TaxExemptions",
  "PlatformPhoneNumbers",
  "OrganisationSettings",
  "SystemWorkflows",
  "CallingRules",
] as const;

export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: axiosBaseQuery({
    baseUrl: `${DialerTreeConfig.baseAPIEndpoint}`,
  }),
  tagTypes: adminApiTagTypes,
  endpoints: (builder) => ({
    getPlatformUsers: builder.query<User[], void>({
      query: () => ({
        url: "/v1/platform-admin/users",
        method: "get",
      }),
      providesTags: ["PlatformUsers"],
    }),
    loginAsUser: builder.mutation<User, string>({
      query: (userId) => ({
        url: `/v1/platform-admin/users/${userId}/login-as`,
        method: "post",
      }),
    }),
    ensureA2pEventStream: builder.mutation<A2pEventStreamStatus, void>({
      query: () => ({
        url: "/v1/platform-admin/a2p/event-stream",
        method: "post",
      }),
    }),
    getA2pAdminRegistrations: builder.query<A2pRegistration[], string | void>({
      query: (status) => ({
        url: "/v1/platform-admin/a2p-registrations",
        method: "get",
        params: status ? { status } : undefined,
      }),
      providesTags: ["A2pAdminRegistrations"],
    }),
    approveA2pRegistration: builder.mutation<A2pRegistration, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/a2p-registrations/${organisationId}/approve`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pAdminRegistrations"]),
    }),
    rejectA2pRegistration: builder.mutation<A2pRegistration, { organisationId: string; note: string }>({
      query: ({ organisationId, note }) => ({
        url: `/v1/platform-admin/a2p-registrations/${organisationId}/reject`,
        method: "post",
        data: { note },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pAdminRegistrations"]),
    }),
    refreshA2pAdminRegistration: builder.mutation<A2pRegistration, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/a2p-registrations/${organisationId}/refresh`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pAdminRegistrations"]),
    }),
    resyncA2pRegistration: builder.mutation<A2pRegistration, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/a2p-registrations/${organisationId}/resync`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pAdminRegistrations"]),
    }),
    clearA2pRegistrationSid: builder.mutation<A2pRegistration, { organisationId: string; field: string }>({
      query: ({ organisationId, field }) => ({
        url: `/v1/platform-admin/a2p-registrations/${organisationId}/clear-sid`,
        method: "post",
        data: { field },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["A2pAdminRegistrations"]),
    }),
    applyA2pRecurringBilling: builder.mutation<A2pRegistration, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/a2p-registrations/${organisationId}/apply-recurring`,
        method: "post",
      }),
    }),
    getTaxExemptions: builder.query<TaxExemption[], string | void>({
      query: (status) => ({
        url: "/v1/platform-admin/tax-exemptions",
        method: "get",
        params: status ? { status } : undefined,
      }),
      providesTags: ["TaxExemptions"],
    }),
    approveTaxExemption: builder.mutation<TaxExemption, { exemptionId: string; expiresOn: string | null }>({
      query: ({ exemptionId, expiresOn }) => ({
        url: `/v1/platform-admin/tax-exemptions/${exemptionId}/approve`,
        method: "post",
        data: { expiresOn },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["TaxExemptions"]),
    }),
    rejectTaxExemption: builder.mutation<TaxExemption, { exemptionId: string; reason: string }>({
      query: ({ exemptionId, reason }) => ({
        url: `/v1/platform-admin/tax-exemptions/${exemptionId}/reject`,
        method: "post",
        data: { reason },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["TaxExemptions"]),
    }),
    getPlatformPhoneNumbers: builder.query<PhoneNumber[], void>({
      query: () => ({
        url: "/v1/platform-admin/phone-numbers",
        method: "get",
      }),
      providesTags: ["PlatformPhoneNumbers"],
    }),
    registerNumberReputation: builder.mutation<PhoneNumber, string>({
      query: (phoneNumberId) => ({
        url: `/v1/platform-admin/phone-numbers/${phoneNumberId}/reputation/register`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["PlatformPhoneNumbers"]),
    }),
    flagNumberReputation: builder.mutation<PhoneNumber, string>({
      query: (phoneNumberId) => ({
        url: `/v1/platform-admin/phone-numbers/${phoneNumberId}/reputation/flag`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["PlatformPhoneNumbers"]),
    }),
    appealNumberReputation: builder.mutation<PhoneNumber, string>({
      query: (phoneNumberId) => ({
        url: `/v1/platform-admin/phone-numbers/${phoneNumberId}/reputation/appeal`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["PlatformPhoneNumbers"]),
    }),
    refreshOrganisationReputation: builder.mutation<{ status: string }, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/reputation/refresh`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["PlatformPhoneNumbers"]),
    }),
    getRegistrationRequests: builder.query<RegistrationRequest[], string | void>({
      query: (status) => ({
        url: "/v1/platform-admin/registration-requests",
        method: "get",
        params: status ? { status } : undefined,
      }),
      providesTags: ["RegistrationRequests"],
    }),
    approveRegistrationRequest: builder.mutation<RegistrationRequest, string>({
      query: (id) => ({
        url: `/v1/platform-admin/registration-requests/${id}/approve`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["RegistrationRequests"]),
    }),
    rejectRegistrationRequest: builder.mutation<RegistrationRequest, string>({
      query: (id) => ({
        url: `/v1/platform-admin/registration-requests/${id}/reject`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["RegistrationRequests"]),
    }),

    getBlockedEmailDomains: builder.query<BlockedEmailDomain[], void>({
      query: () => ({
        url: "/v1/platform-admin/blocked-email-domains",
        method: "get",
      }),
      providesTags: ["BlockedEmailDomains"],
    }),
    addBlockedEmailDomain: builder.mutation<BlockedEmailDomain, BlockedEmailDomainData>({
      query: (data) => ({
        url: "/v1/platform-admin/blocked-email-domains",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["BlockedEmailDomains"]),
    }),
    deleteBlockedEmailDomain: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/platform-admin/blocked-email-domains/${id}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["BlockedEmailDomains"]),
    }),

    getCallingRules: builder.query<CallingRule[], void>({
      query: () => ({
        url: "/v1/platform-admin/calling-rules",
        method: "get",
      }),
      providesTags: ["CallingRules"],
    }),
    createCallingRule: builder.mutation<CallingRule, CallingRuleFormData>({
      query: (data) => ({
        url: "/v1/platform-admin/calling-rules",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CallingRules"]),
    }),
    updateCallingRule: builder.mutation<CallingRule, { ruleId: string; data: CallingRuleFormData }>({
      query: ({ ruleId, data }) => ({
        url: `/v1/platform-admin/calling-rules/${ruleId}`,
        method: "put",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CallingRules"]),
    }),
    deleteCallingRule: builder.mutation<void, string>({
      query: (ruleId) => ({
        url: `/v1/platform-admin/calling-rules/${ruleId}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CallingRules"]),
    }),
    getCallingRegions: builder.query<CallingRegionOption[], void>({
      query: () => ({
        url: "/v1/platform-admin/calling-rules/regions",
        method: "get",
      }),
    }),
    getCallingHolidays: builder.query<CallingHoliday[], void>({
      query: () => ({
        url: "/v1/platform-admin/calling-rules/holidays",
        method: "get",
      }),
      providesTags: ["CallingRules"],
    }),
    addCallingHoliday: builder.mutation<CallingHoliday, CallingHolidayFormData>({
      query: (data) => ({
        url: "/v1/platform-admin/calling-rules/holidays",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CallingRules"]),
    }),
    deleteCallingHoliday: builder.mutation<void, string>({
      query: (holidayId) => ({
        url: `/v1/platform-admin/calling-rules/holidays/${holidayId}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["CallingRules"]),
    }),

    getFraudSignals: builder.query<FraudSignal[], string | void>({
      query: (status) => ({
        url: "/v1/platform-admin/fraud-signals",
        method: "get",
        params: status ? { status } : undefined,
      }),
      providesTags: ["FraudSignals"],
    }),
    reviewFraudSignal: builder.mutation<FraudSignal, string>({
      query: (id) => ({
        url: `/v1/platform-admin/fraud-signals/${id}/review`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["FraudSignals"]),
    }),
    dismissFraudSignal: builder.mutation<FraudSignal, string>({
      query: (id) => ({
        url: `/v1/platform-admin/fraud-signals/${id}/dismiss`,
        method: "post",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["FraudSignals"]),
    }),

    getPlatformAdminOrganisations: builder.query<Organisation[], void>({
      query: () => ({
        url: "/v1/platform-admin/organisations",
        method: "get",
      }),
      providesTags: ["PlatformAdmin"],
    }),

    searchOrganisations: builder.query<Organisation[], string>({
      query: (search) => ({
        url: `/v1/platform-admin/organisations?search=${encodeURIComponent(search)}`,
        method: "get",
      }),
    }),

    getPlatformAdminOrganisation: builder.query<Organisation, string>({
      query: (id) => ({
        url: `/v1/platform-admin/organisations/${id}`,
        method: "get",
      }),
      providesTags: ["PlatformAdmin"],
    }),

    getOrganisationSettings: builder.query<OrganisationSettingsOverview, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/settings`,
        method: "get",
      }),
      providesTags: ["OrganisationSettings"],
    }),

    setOrganisationTranscriptionProvider: builder.mutation<
      OrganisationSettingsOverview,
      { organisationId: string; provider: string | null }
    >({
      query: ({ organisationId, provider }) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/settings/transcription-provider`,
        method: "put",
        data: { value: provider },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["OrganisationSettings"]),
    }),

    setOrganisationQuietHoursPolicy: builder.mutation<
      OrganisationSettingsOverview,
      // null clears the override and returns the org to the platform default.
      { organisationId: string; policy: string | null }
    >({
      query: ({ organisationId, policy }) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/settings/quiet-hours-policy`,
        method: "put",
        data: { value: policy },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["OrganisationSettings"]),
    }),

    setOrganisationPowerDialerCallbackMessage: builder.mutation<
      OrganisationSettingsOverview,
      // An empty message puts the organisation back on the built-in default.
      { organisationId: string; message: string }
    >({
      query: ({ organisationId, message }) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/settings/power-dialer-callback-message`,
        method: "put",
        data: { value: message },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["OrganisationSettings"]),
    }),

    setOrganisationPowerDialerOptOutDigit: builder.mutation<
      OrganisationSettingsOverview,
      { organisationId: string; optOutDigit: string }
    >({
      query: ({ organisationId, optOutDigit }) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/settings/power-dialer-opt-out-digit`,
        method: "put",
        data: { value: optOutDigit },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["OrganisationSettings"]),
    }),

    setOrganisationTranscriptionEnabled: builder.mutation<
      OrganisationSettingsOverview,
      { organisationId: string; enabled: boolean }
    >({
      query: ({ organisationId, enabled }) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/settings/transcription-enabled`,
        method: "put",
        data: { value: settingState(enabled) },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["OrganisationSettings"]),
    }),

    getOrganisationSystemWorkflows: builder.query<SystemWorkflowStatus[], string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/workflows/system`,
        method: "get",
      }),
      providesTags: ["SystemWorkflows"],
    }),

    installOrganisationSystemWorkflows: builder.mutation<
      SystemWorkflowStatus[],
      { organisationId: string; override: boolean }
    >({
      query: ({ organisationId, override }) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/workflows/install`,
        method: "post",
        params: { override },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["SystemWorkflows"]),
    }),

    installOrganisationSystemWorkflow: builder.mutation<
      SystemWorkflowStatus[],
      { organisationId: string; workflowKey: string; override: boolean }
    >({
      query: ({ organisationId, workflowKey, override }) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/workflows/${workflowKey}/install`,
        method: "post",
        params: { override },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["SystemWorkflows"]),
    }),

    installSystemWorkflows: builder.mutation<{ organisations: number }, { override: boolean }>({
      query: ({ override }) => ({
        url: "/v1/platform-admin/workflows/install",
        method: "post",
        params: { override },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["SystemWorkflows"]),
    }),

    fixTwilioCredentials: builder.mutation<{ status: string; message: string; accountSid: string }, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/fix-twilio`,
        method: "post",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["PlatformAdmin"]),
    }),
    fixMobileCredentials: builder.mutation<{ status: string; message: string }, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/fix-mobile-credentials`,
        method: "post",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["PlatformAdmin"]),
    }),
    getOrganisationCharges: builder.query<
      PagedResponse<Charge>,
      { organisationId: string; page?: number; size?: number; type?: string }
    >({
      query: ({ organisationId, page = 0, size = 25, type }) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/charges`,
        method: "get",
        params: { page, size, sort: "createdAt,desc", ...(type ? { type } : {}) },
      }),
      providesTags: ["PlatformCharges"],
    }),
    getOrganisationCharge: builder.query<Charge, { organisationId: string; chargeId: string }>({
      query: ({ organisationId, chargeId }) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/charges/${chargeId}`,
        method: "get",
      }),
      providesTags: ["PlatformCharges"],
    }),
    getOrganisationInvoices: builder.query<PagedResponse<Invoice>, { organisationId: string; page?: number; size?: number }>({
      query: ({ organisationId, page = 0, size = 25 }) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/invoices`,
        method: "get",
        params: { page, size },
      }),
      providesTags: ["PlatformInvoices"],
    }),
    voidInvoice: builder.mutation<Invoice, string>({
      query: (invoiceId) => ({
        url: `/v1/platform-admin/invoices/${invoiceId}/void`,
        method: "post",
      }),
      invalidatesTags: (result, error) => (error ? [] : ["PlatformInvoices"]),
    }),
    addOrganisationCredit: builder.mutation<Deposit, { organisationId: string; amount: string; reason: string }>({
      query: ({ organisationId, amount, reason }) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/credits`,
        method: "post",
        data: { amount, reason },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["PlatformAdmin"]),
    }),
    getPlatformConfig: builder.query<PlatformConfig, void>({
      query: () => ({ url: `/v1/platform-admin/config`, method: "get" }),
      providesTags: ["PlatformConfig"],
    }),
    updatePlatformConfig: builder.mutation<void, Partial<Record<string, string>>>({
      query: (data) => ({ url: `/v1/platform-admin/config`, method: "put", data }),
      // Also refreshes every organisation's settings page, which shows the policy they inherit.
      invalidatesTags: (_, error) => (error ? [] : ["PlatformConfig", "OrganisationSettings"]),
    }),
    getQuietHoursPolicyOptions: builder.query<QuietHoursPolicyOption[], void>({
      query: () => ({ url: `/v1/platform-admin/config/quiet-hours-policies`, method: "get" }),
    }),
    syncTwimlUrl: builder.mutation<void, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/sync-twiml-url`,
        method: "post",
      }),
    }),
    syncSmsUrl: builder.mutation<void, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/sync-sms-url`,
        method: "post",
      }),
    }),
    toggleA2pMockBrand: builder.mutation<{ mockBrand: boolean }, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/a2p-mock`,
        method: "put",
      }),
    }),

    setBillingExempt: builder.mutation<Organisation, { organisationId: string; exempt: boolean }>({
      query: ({ organisationId, exempt }) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/billing-exempt`,
        method: "put",
        params: { exempt },
      }),
    }),

    assignPlan: builder.mutation<PlanAssignment, { organisationId: string; plan: string; validUntil?: string | null }>({
      query: ({ organisationId, plan, validUntil }) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/plan`,
        method: "post",
        data: { plan, validUntil: validUntil || null },
      }),
    }),

    revokePlan: builder.mutation<PlanAssignment, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/plan/revoke`,
        method: "post",
      }),
    }),

    getOrganisationSubscription: builder.query<StripeSubscriptionData, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/subscription`,
        method: "get",
      }),
    }),

    clearPlanInvitation: builder.mutation<Organisation, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/plan-invitation`,
        method: "delete",
      }),
    }),

    getBillingEvents: builder.query<
      PagedResponse<BillingEvent>,
      { organisationId?: string; page?: number; size?: number }
    >({
      query: (params) => ({
        url: `/v1/platform-admin/billing-events`,
        method: "get",
        params,
      }),
    }),

    getAdminWorkflowExecutions: builder.query<
      PagedResponse<AdminWorkflowExecution>,
      { organisationId?: string; page?: number; size?: number }
    >({
      query: (params) => ({
        url: `/v1/platform-admin/workflow-executions`,
        method: "get",
        params,
      }),
    }),

    getAdminWorkflowExecution: builder.query<AdminWorkflowExecution, string>({
      query: (executionId) => ({
        url: `/v1/platform-admin/workflow-executions/${executionId}`,
        method: "get",
      }),
    }),

    getAdminWorkflows: builder.query<
      PagedResponse<AdminWorkflow>,
      { organisationId?: string; page?: number; size?: number }
    >({
      query: (params) => ({
        url: `/v1/platform-admin/workflows`,
        method: "get",
        params,
      }),
    }),

    // Pricing Plans
    getPricingPlans: builder.query<PricingPlan[], void>({
      query: () => ({ url: "/v1/platform-admin/pricing-plans", method: "get" }),
      providesTags: ["PricingPlans"],
    }),
    getPricingPlan: builder.query<PricingPlan, number>({
      query: (id) => ({ url: `/v1/platform-admin/pricing-plans/${id}`, method: "get" }),
      providesTags: ["PricingPlans"],
    }),
    createPricingPlan: builder.mutation<PricingPlan, Partial<PricingPlan>>({
      query: (data) => ({ url: "/v1/platform-admin/pricing-plans", method: "post", data }),
      invalidatesTags: (_, error) => (error ? [] : ["PricingPlans"]),
    }),
    updatePricingPlan: builder.mutation<PricingPlan, { id: number; data: Partial<PricingPlan> }>({
      query: ({ id, data }) => ({ url: `/v1/platform-admin/pricing-plans/${id}`, method: "put", data }),
      invalidatesTags: (_, error) => (error ? [] : ["PricingPlans"]),
    }),
    deletePricingPlan: builder.mutation<void, number>({
      query: (id) => ({ url: `/v1/platform-admin/pricing-plans/${id}`, method: "delete" }),
      invalidatesTags: (_, error) => (error ? [] : ["PricingPlans"]),
    }),

    // Subscription Plans
    getSubscriptionPlans: builder.query<SubscriptionPlan[], void>({
      query: () => ({ url: "/v1/platform-admin/subscription-plans", method: "get" }),
      providesTags: ["SubscriptionPlans"],
    }),
    getPlanFeatureOptions: builder.query<PlanFeatureOption[], void>({
      query: () => ({ url: "/v1/platform-admin/features", method: "get" }),
    }),
    getAddOnCatalog: builder.query<AddOnCatalogEntry[], void>({
      query: () => ({ url: "/v1/platform-admin/add-ons", method: "get" }),
      providesTags: ["AddOns"],
    }),
    updateAddOnPrice: builder.mutation<AddOnCatalogEntry, { lookupKey: string; monthlyCostCents: number }>({
      query: ({ lookupKey, monthlyCostCents }) => ({
        url: `/v1/platform-admin/add-ons/${lookupKey}`,
        method: "put",
        data: { monthlyCostCents },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["AddOns"]),
    }),
    createSubscriptionPlan: builder.mutation<SubscriptionPlan, Partial<SubscriptionPlan>>({
      query: (data) => ({ url: "/v1/platform-admin/subscription-plans", method: "post", data }),
      invalidatesTags: (_, error) => (error ? [] : ["SubscriptionPlans"]),
    }),
    updateSubscriptionPlan: builder.mutation<SubscriptionPlan, { id: number; data: Partial<SubscriptionPlan> }>({
      query: ({ id, data }) => ({ url: `/v1/platform-admin/subscription-plans/${id}`, method: "put", data }),
      invalidatesTags: (_, error) => (error ? [] : ["SubscriptionPlans"]),
    }),
    deleteSubscriptionPlan: builder.mutation<void, number>({
      query: (id) => ({ url: `/v1/platform-admin/subscription-plans/${id}`, method: "delete" }),
      invalidatesTags: (_, error) => (error ? [] : ["SubscriptionPlans"]),
    }),

    // Assign Pricing Plan to Organisation
    assignPricingPlan: builder.mutation<Organisation, { orgId: string; planId: number }>({
      query: ({ orgId, planId }) => ({
        url: `/v1/platform-admin/organisations/${orgId}/pricing-plan/${planId}`,
        method: "put",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["PlatformAdmin"]),
    }),
    resetPricingPlan: builder.mutation<Organisation, string>({
      query: (orgId) => ({
        url: `/v1/platform-admin/organisations/${orgId}/pricing-plan`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["PlatformAdmin"]),
    }),

    // AI Agent Templates
    getAiTemplates: builder.query<
      PagedResponse<AiAgentTemplate>,
      { status?: string; page?: number; size?: number } | void
    >({
      query: ({ status, page = 0, size = 25 } = {}) => ({
        url: "/v1/platform-admin/ai-templates",
        method: "get",
        params: { page, size, ...(status ? { status } : {}) },
      }),
      providesTags: ["AiTemplates"],
    }),
    getAiTemplate: builder.query<AiAgentTemplate, string>({
      query: (id) => ({ url: `/v1/platform-admin/ai-templates/${id}`, method: "get" }),
      providesTags: ["AiTemplates"],
    }),
    createAiTemplate: builder.mutation<AiAgentTemplate, Partial<AiAgentTemplate>>({
      query: (data) => ({ url: "/v1/platform-admin/ai-templates", method: "post", data }),
      invalidatesTags: (_, error) => (error ? [] : ["AiTemplates"]),
    }),
    updateAiTemplate: builder.mutation<AiAgentTemplate, { id: string; data: Partial<AiAgentTemplate> }>({
      query: ({ id, data }) => ({ url: `/v1/platform-admin/ai-templates/${id}`, method: "put", data }),
      invalidatesTags: (_, error) => (error ? [] : ["AiTemplates"]),
    }),
    deleteAiTemplate: builder.mutation<void, string>({
      query: (id) => ({ url: `/v1/platform-admin/ai-templates/${id}`, method: "delete" }),
      invalidatesTags: (_, error) => (error ? [] : ["AiTemplates"]),
    }),
    archiveAiTemplate: builder.mutation<AiAgentTemplate, string>({
      query: (id) => ({ url: `/v1/platform-admin/ai-templates/${id}/archive`, method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["AiTemplates", "AiListings"]),
    }),
    restoreAiTemplate: builder.mutation<AiAgentTemplate, string>({
      query: (id) => ({ url: `/v1/platform-admin/ai-templates/${id}/restore`, method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["AiTemplates"]),
    }),
    // Assign a library prompt version as a template's default (null clears it).
    setTemplatePrompt: builder.mutation<AiAgentTemplate, { id: string; promptVersionId: string | null }>({
      query: ({ id, promptVersionId }) => ({
        url: `/v1/platform-admin/ai-templates/${id}/prompt`,
        method: "patch",
        data: { promptVersionId },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["AiTemplates"]),
    }),

    // AI Agent Listings
    getAiListings: builder.query<
      PagedResponse<AiAgentListing>,
      { listed?: boolean; page?: number; size?: number } | void
    >({
      query: ({ listed, page = 0, size = 25 } = {}) => ({
        url: "/v1/platform-admin/ai-listings",
        method: "get",
        params: { page, size, ...(listed === undefined ? {} : { listed }) },
      }),
      providesTags: ["AiListings"],
    }),
    createAiListing: builder.mutation<AiAgentListing, Partial<AiAgentListing>>({
      query: (data) => ({ url: "/v1/platform-admin/ai-listings", method: "post", data }),
      invalidatesTags: (_, error) => (error ? [] : ["AiListings"]),
    }),
    updateAiListing: builder.mutation<AiAgentListing, { id: string; data: Partial<AiAgentListing> }>({
      query: ({ id, data }) => ({ url: `/v1/platform-admin/ai-listings/${id}`, method: "put", data }),
      invalidatesTags: (_, error) => (error ? [] : ["AiListings"]),
    }),
    deleteAiListing: builder.mutation<void, string>({
      query: (id) => ({ url: `/v1/platform-admin/ai-listings/${id}`, method: "delete" }),
      invalidatesTags: (_, error) => (error ? [] : ["AiListings"]),
    }),
    unlistAiListing: builder.mutation<AiAgentListing, string>({
      query: (id) => ({ url: `/v1/platform-admin/ai-listings/${id}/unlist`, method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["AiListings"]),
    }),
    relistAiListing: builder.mutation<AiAgentListing, string>({
      query: (id) => ({ url: `/v1/platform-admin/ai-listings/${id}/relist`, method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["AiListings"]),
    }),

    // Voice models (native AI provider dropdowns)
    getVoiceModelsCatalogue: builder.query<VoiceModelsCatalogue, void>({
      query: () => ({ url: "/v1/platform-admin/voice-models", method: "get" }),
      providesTags: ["VoiceModels"],
    }),
    getSttModels: builder.query<VoiceModelOption[], void>({
      query: () => ({ url: "/v1/platform-admin/voice-models/stt", method: "get" }),
      providesTags: ["VoiceModels"],
    }),
    getLlmModels: builder.query<VoiceModelOption[], void>({
      query: () => ({ url: "/v1/platform-admin/voice-models/llm", method: "get" }),
      providesTags: ["VoiceModels"],
    }),
    getLlmModelAvailability: builder.query<Record<string, string[]>, void>({
      query: () => ({ url: "/v1/platform-admin/voice-models/llm-availability", method: "get" }),
      providesTags: ["VoiceModels"],
    }),
    getRecentLogs: builder.query<LogEntry[], void>({
      query: () => ({ url: "/v1/platform-admin/logs/recent", method: "get" }),
    }),
    getTtsModels: builder.query<VoiceModelOption[], void>({
      query: () => ({ url: "/v1/platform-admin/voice-models/tts", method: "get" }),
      providesTags: ["VoiceModels"],
    }),
    getVoices: builder.query<VoiceOption[], void>({
      query: () => ({ url: "/v1/platform-admin/voice-models/voices", method: "get" }),
      providesTags: ["VoiceModels"],
    }),
    refreshVoiceModels: builder.mutation<VoiceModelsCatalogue, void>({
      query: () => ({ url: "/v1/platform-admin/voice-models/refresh", method: "post" }),
      invalidatesTags: (_, error) => (error ? [] : ["VoiceModels"]),
    }),

    // Deployed agents (cross-org runtime overrides)
    getDeployedAgents: builder.query<PagedResponse<AiAgent>, { page?: number; size?: number }>({
      query: ({ page = 0, size = 50 } = {}) => ({
        url: "/v1/platform-admin/ai-agents/deployed",
        method: "get",
        params: { page, size, sort: "createdAt,desc" },
      }),
      providesTags: ["DeployedAgents"],
    }),
    getDeployedAgent: builder.query<AiAgent, string>({
      query: (agentId) => ({
        url: `/v1/platform-admin/ai-agents/deployed/${agentId}`,
        method: "get",
      }),
      providesTags: ["DeployedAgents"],
    }),
    updateDeployedAgentRuntime: builder.mutation<
      AiAgent,
      { agentId: string; orchestrator: string; voiceProvider?: string | null; voiceId?: string | null; llmProvider?: string | null; llmModel?: string | null; customConfig?: Record<string, any> | null; tools?: Record<string, any> | null; declaredOutcomes?: string[] | null; skills?: string[] | null; greeting?: string | null; closingMessage?: string | null }
    >({
      query: ({ agentId, ...data }) => ({
        url: `/v1/platform-admin/ai-agents/deployed/${agentId}`,
        method: "patch",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DeployedAgents"]),
    }),
    // Grant a complimentary (free, non-billed) agent to an organisation — from a template or by
    // copying another deployed agent's config. For test orgs; skips payment entirely.
    createComplimentaryAgent: builder.mutation<
      AiAgent,
      {
        organisationId: string;
        templateId?: string;
        sourceAgentId?: string;
        name?: string;
        greeting?: string;
        orchestrator?: string;
        voiceProvider?: string;
        voiceId?: string;
        llmProvider?: string;
        llmModel?: string;
        direction?: string;
      }
    >({
      query: (data) => ({
        url: "/v1/platform-admin/ai-agents/deployed",
        method: "post",
        data,
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DeployedAgents"]),
    }),
    deleteDeployedAgent: builder.mutation<void, string>({
      query: (agentId) => ({
        url: `/v1/platform-admin/ai-agents/deployed/${agentId}`,
        method: "delete",
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DeployedAgents"]),
    }),
    renameDeployedAgent: builder.mutation<AiAgent, { agentId: string; name: string }>({
      query: ({ agentId, name }) => ({
        url: `/v1/platform-admin/ai-agents/deployed/${agentId}/name`,
        method: "patch",
        data: { name },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DeployedAgents"]),
    }),
    // Assign a specific library prompt version to an agent (null/blank clears → template default).
    setDeployedAgentPrompt: builder.mutation<AiAgent, { agentId: string; promptVersionId: string | null }>({
      query: ({ agentId, promptVersionId }) => ({
        url: `/v1/platform-admin/ai-agents/deployed/${agentId}/prompt`,
        method: "patch",
        data: { promptVersionId },
      }),
      invalidatesTags: (_, error) => (error ? [] : ["DeployedAgents"]),
    }),

    // ── Shared prompt library ─────────────────────────────────────────────
    // Omitting the kind returns AGENT prompts, so the persona pickers are unaffected by skills.
    getPrompts: builder.query<
      PagedResponse<Prompt>,
      { kind?: PromptKind; archived?: boolean; unpublished?: boolean; search?: string; page?: number; size?: number } | void
    >({
      query: ({ kind, archived, unpublished, search, page = 0, size = 25 } = {}) => ({
        url: "/v1/platform-admin/prompts",
        method: "get",
        params: {
          page,
          size,
          ...(kind ? { kind } : {}),
          ...(archived ? { archived } : {}),
          ...(unpublished ? { unpublished } : {}),
          ...(search ? { search } : {}),
        },
      }),
      providesTags: ["Prompts"],
    }),
    getPrompt: builder.query<Prompt, string>({
      query: (id) => ({ url: `/v1/platform-admin/prompts/${id}`, method: "get" }),
      providesTags: (_r, _e, id) => [{ type: "Prompt", id }],
    }),
    createPrompt: builder.mutation<Prompt, { name: string; description?: string; systemPrompt: string; versionLabel?: string; kind?: PromptKind; skillType?: SkillType; skillScope?: SkillScope; skillAudience?: SkillAudience; memberTools?: string[]; handoffDescription?: string; openingLine?: string }>({
      query: (data) => ({ url: "/v1/platform-admin/prompts", method: "post", data }),
      invalidatesTags: (_, error) => (error ? [] : ["Prompts"]),
    }),
    updatePrompt: builder.mutation<Prompt, { id: string; name?: string; description?: string; skillType?: SkillType; skillScope?: SkillScope; skillAudience?: SkillAudience; memberTools?: string[]; handoffDescription?: string; openingLine?: string }>({
      query: ({ id, ...data }) => ({ url: `/v1/platform-admin/prompts/${id}`, method: "patch", data }),
      invalidatesTags: (_r, error, { id }) => (error ? [] : ["Prompts", { type: "Prompt", id }]),
    }),
    archivePrompt: builder.mutation<Prompt, string>({
      query: (id) => ({ url: `/v1/platform-admin/prompts/${id}/archive`, method: "post" }),
      invalidatesTags: (_r, error, id) => (error ? [] : ["Prompts", { type: "Prompt", id }]),
    }),
    restorePrompt: builder.mutation<Prompt, string>({
      query: (id) => ({ url: `/v1/platform-admin/prompts/${id}/restore`, method: "post" }),
      invalidatesTags: (_r, error, id) => (error ? [] : ["Prompts", { type: "Prompt", id }]),
    }),
    addPromptVersion: builder.mutation<PromptVersion, { promptId: string; systemPrompt: string; label?: string }>({
      query: ({ promptId, ...data }) => ({ url: `/v1/platform-admin/prompts/${promptId}/versions`, method: "post", data }),
      invalidatesTags: (_r, error, { promptId }) => (error ? [] : [{ type: "Prompt", id: promptId }, "Prompts"]),
    }),
    updatePromptVersionStatus: builder.mutation<PromptVersion, { promptId: string; versionId: string; status: string }>({
      query: ({ promptId, versionId, status }) => ({
        url: `/v1/platform-admin/prompts/${promptId}/versions/${versionId}`,
        method: "patch",
        data: { status },
      }),
      invalidatesTags: (_r, error, { promptId }) => (error ? [] : [{ type: "Prompt", id: promptId }]),
    }),

    // Admin test triggers
    testTriggerWorkflow: builder.mutation<
      WorkflowExecution,
      { workflowId: string; contactId?: string; dealId?: string }
    >({
      query: ({ workflowId, ...body }) => ({
        url: `/v1/platform-admin/workflows/${workflowId}/test-trigger`,
        method: "post",
        data: body,
      }),
    }),
    testPlaceAiCall: builder.mutation<
      Call,
      {
        organisationId: string;
        agentId: string;
        toPhoneNumber: string;
        fromPhoneNumberId?: string | null;
        contactId?: string | null;
        callScope?: string | null;
        variableOverrides?: Record<string, any> | null;
        maxDurationSeconds?: number | null;
      }
    >({
      query: (body) => ({
        url: "/v1/platform-admin/test/place-ai-call",
        method: "post",
        data: body,
      }),
    }),

    getOrgAddOnPrices: builder.query<OrgAddOnPriceEntry[], string>({
      query: (orgId) => ({
        url: `/v1/platform-admin/organisations/${orgId}/add-on-prices`,
        method: "get",
      }),
      providesTags: (_, __, orgId) => [{ type: "OrgAddOnPrices", id: orgId }],
    }),
    setOrgAddOnPrice: builder.mutation<
      OrgAddOnPriceEntry[],
      { orgId: string; addOnType: AddOnType; monthlyCostCents: number }
    >({
      query: ({ orgId, addOnType, monthlyCostCents }) => ({
        url: `/v1/platform-admin/organisations/${orgId}/add-on-prices/${addOnType}`,
        method: "put",
        data: { monthlyCostCents },
      }),
      invalidatesTags: (_, error, { orgId }) =>
        error ? [] : [{ type: "OrgAddOnPrices", id: orgId }],
    }),
    clearOrgAddOnPrice: builder.mutation<
      OrgAddOnPriceEntry[],
      { orgId: string; addOnType: AddOnType }
    >({
      query: ({ orgId, addOnType }) => ({
        url: `/v1/platform-admin/organisations/${orgId}/add-on-prices/${addOnType}`,
        method: "delete",
      }),
      invalidatesTags: (_, error, { orgId }) =>
        error ? [] : [{ type: "OrgAddOnPrices", id: orgId }],
    }),
    getOrganisationUsageRates: builder.query<PricingPlan, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/usage-rates`,
        method: "get",
      }),
      providesTags: (_, __, organisationId) => [{ type: "OrganisationUsageRates", id: organisationId }],
    }),
    setOrganisationUsageRates: builder.mutation<PricingPlan, { organisationId: string; plan: PricingPlan }>({
      query: ({ organisationId, plan }) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/usage-rates`,
        method: "put",
        data: plan,
      }),
      invalidatesTags: (_, error, { organisationId }) =>
        error ? [] : [{ type: "OrganisationUsageRates", id: organisationId }],
    }),
    clearOrganisationUsageRates: builder.mutation<PricingPlan, string>({
      query: (organisationId) => ({
        url: `/v1/platform-admin/organisations/${organisationId}/usage-rates`,
        method: "delete",
      }),
      invalidatesTags: (_, error, organisationId) =>
        error ? [] : [{ type: "OrganisationUsageRates", id: organisationId }],
    }),

    getAiMetrics: builder.query<AiMetricsResponse, void>({
      query: () => ({
        url: "/v1/platform-admin/metrics/ai",
        method: "get",
      }),
    }),
  }),
});

export const {
  useGetPlatformUsersQuery,
  useLoginAsUserMutation,
  useEnsureA2pEventStreamMutation,
  useGetA2pAdminRegistrationsQuery,
  useApproveA2pRegistrationMutation,
  useRejectA2pRegistrationMutation,
  useRefreshA2pAdminRegistrationMutation,
  useResyncA2pRegistrationMutation,
  useClearA2pRegistrationSidMutation,
  useApplyA2pRecurringBillingMutation,
  useGetTaxExemptionsQuery,
  useApproveTaxExemptionMutation,
  useRejectTaxExemptionMutation,
  useGetPlatformPhoneNumbersQuery,
  useRegisterNumberReputationMutation,
  useFlagNumberReputationMutation,
  useAppealNumberReputationMutation,
  useRefreshOrganisationReputationMutation,
  useSetBillingExemptMutation,
  useAssignPlanMutation,
  useRevokePlanMutation,
  useGetOrganisationSubscriptionQuery,
  useClearPlanInvitationMutation,
  useGetBillingEventsQuery,
  useGetAdminWorkflowExecutionsQuery,
  useGetAdminWorkflowExecutionQuery,
  useGetAdminWorkflowsQuery,
  useGetRegistrationRequestsQuery,
  useApproveRegistrationRequestMutation,
  useRejectRegistrationRequestMutation,
  useGetBlockedEmailDomainsQuery,
  useAddBlockedEmailDomainMutation,
  useDeleteBlockedEmailDomainMutation,
  useGetCallingRulesQuery,
  useCreateCallingRuleMutation,
  useUpdateCallingRuleMutation,
  useDeleteCallingRuleMutation,
  useGetCallingRegionsQuery,
  useGetCallingHolidaysQuery,
  useAddCallingHolidayMutation,
  useDeleteCallingHolidayMutation,
  useGetFraudSignalsQuery,
  useReviewFraudSignalMutation,
  useDismissFraudSignalMutation,
  useGetPlatformAdminOrganisationsQuery,
  useSearchOrganisationsQuery,
  useGetPlatformAdminOrganisationQuery,
  useGetOrganisationSettingsQuery,
  useSetOrganisationTranscriptionProviderMutation,
  useSetOrganisationTranscriptionEnabledMutation,
  useSetOrganisationQuietHoursPolicyMutation,
  useSetOrganisationPowerDialerCallbackMessageMutation,
  useSetOrganisationPowerDialerOptOutDigitMutation,
  useGetOrganisationSystemWorkflowsQuery,
  useInstallOrganisationSystemWorkflowsMutation,
  useInstallOrganisationSystemWorkflowMutation,
  useInstallSystemWorkflowsMutation,
  useFixTwilioCredentialsMutation,
  useFixMobileCredentialsMutation,
  useGetOrganisationChargesQuery,
  useGetOrganisationChargeQuery,
  useGetOrganisationInvoicesQuery,
  useVoidInvoiceMutation,
  useAddOrganisationCreditMutation,
  useUpdatePlatformConfigMutation,
  useGetQuietHoursPolicyOptionsQuery,
  useGetPlatformConfigQuery,
  useSyncTwimlUrlMutation,
  useSyncSmsUrlMutation,
  useToggleA2pMockBrandMutation,
  useGetPricingPlansQuery,
  useGetPricingPlanQuery,
  useCreatePricingPlanMutation,
  useUpdatePricingPlanMutation,
  useDeletePricingPlanMutation,
  useGetSubscriptionPlansQuery,
  useCreateSubscriptionPlanMutation,
  useUpdateSubscriptionPlanMutation,
  useDeleteSubscriptionPlanMutation,
  useAssignPricingPlanMutation,
  useResetPricingPlanMutation,
  useGetAiTemplatesQuery,
  useGetAiTemplateQuery,
  useCreateAiTemplateMutation,
  useUpdateAiTemplateMutation,
  useDeleteAiTemplateMutation,
  useArchiveAiTemplateMutation,
  useRestoreAiTemplateMutation,
  useGetAiListingsQuery,
  useCreateAiListingMutation,
  useUpdateAiListingMutation,
  useDeleteAiListingMutation,
  useUnlistAiListingMutation,
  useRelistAiListingMutation,
  useGetVoiceModelsCatalogueQuery,
  useGetSttModelsQuery,
  useGetLlmModelsQuery,
  useGetLlmModelAvailabilityQuery,
  useGetRecentLogsQuery,
  useGetTtsModelsQuery,
  useGetVoicesQuery,
  useRefreshVoiceModelsMutation,
  useGetDeployedAgentsQuery,
  useGetDeployedAgentQuery,
  useUpdateDeployedAgentRuntimeMutation,
  useRenameDeployedAgentMutation,
  useCreateComplimentaryAgentMutation,
  useDeleteDeployedAgentMutation,
  useSetDeployedAgentPromptMutation,
  useSetTemplatePromptMutation,
  useGetPromptsQuery,
  useGetPromptQuery,
  useCreatePromptMutation,
  useUpdatePromptMutation,
  useArchivePromptMutation,
  useRestorePromptMutation,
  useAddPromptVersionMutation,
  useUpdatePromptVersionStatusMutation,
  useTestTriggerWorkflowMutation,
  useTestPlaceAiCallMutation,
  useGetPlanFeatureOptionsQuery,
  useGetAddOnCatalogQuery,
  useUpdateAddOnPriceMutation,
  useGetOrgAddOnPricesQuery,
  useSetOrgAddOnPriceMutation,
  useClearOrgAddOnPriceMutation,
  useGetOrganisationUsageRatesQuery,
  useSetOrganisationUsageRatesMutation,
  useClearOrganisationUsageRatesMutation,
  useGetAiMetricsQuery,
} = adminApi;
